/**
 * @author      Shivani Mehra
 * @date        Oct, 2025
 * @copyright   www.ibirdsservices.com
 */

import * as constants from '../constants/CONSTANT';
import axios from 'axios';
import authApi from './authApi';
import jwt_decode from 'jwt-decode';
import DataApi from './DataApi';

let refreshPromise = null;

export default class WatconnectApi {
  constructor(path) {
    this.token = localStorage.getItem('watconnect_token');
    this.refreshToken = localStorage.getItem('watconnect_refresh_token');
    this.baseUrl = `${constants.Watconnect_API_BASE_URL}/api/${path}`;
  }

  setToken(token, refreshToken) {
    this.token = token;
    this.refreshToken = refreshToken;
    localStorage.setItem('watconnect_token', token);
    localStorage.setItem('watconnect_refresh_token', refreshToken);
  }

  async ensureValidToken() {
    let token = localStorage.getItem('watconnect_token');
    let refreshToken = localStorage.getItem('watconnect_refresh_token');

    this.token = token;
    this.refreshToken = refreshToken;

    if (!token) {
      return;
    }

    let decoded;
    try {
      decoded = jwt_decode(token);
    } catch (e) {
      console.error('Failed to decode WatConnect token:', e);
      return;
    }

    if (!decoded || !decoded.exp) {
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    // Buffer of 30 seconds
    const isExpired = decoded.exp - currentTime < 30;

    if (!isExpired) {
      return;
    }

    if (!refreshToken) {
      console.warn(
        'WatConnect token is expired or expiring, but no refresh token is available.'
      );
      return;
    }

    if (refreshPromise) {
      try {
        await refreshPromise;
        this.token = localStorage.getItem('watconnect_token');
        this.refreshToken = localStorage.getItem('watconnect_refresh_token');
        return;
      } catch (err) {
        throw err;
      }
    }

    refreshPromise = (async () => {
      try {
        const refreshUrl = `${constants.Watconnect_API_BASE_URL}/api/auth/refresh`;
        const res = await axios.post(
          refreshUrl,
          { refreshToken },
          {
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (res.data && res.data.success) {
          const newAuthToken = res.data.authToken;
          const newRefreshToken = res.data.refreshToken;

          this.setToken(newAuthToken, newRefreshToken);

          let emailToMatch;
          try {
            const decodedNew = jwt_decode(newAuthToken);
            emailToMatch = decodedNew.email;
          } catch (err) {
            console.error(
              'Failed to decode new refreshed WatConnect token:',
              err
            );
          }

          if (!emailToMatch) {
            emailToMatch = decoded.email;
          }

          if (emailToMatch) {
            try {
              const dbApi = new DataApi('watconnect');
              await dbApi.post('/watconnect_user/update-tokens', {
                email: emailToMatch,
                access_token: newAuthToken,
                refresh_token: newRefreshToken,
              });
              console.log(
                'WatConnect tokens refreshed and stored in database successfully ✅'
              );
            } catch (dbError) {
              console.warn(
                'Failed to update WatConnect refreshed tokens in database:',
                dbError
              );
            }
          }
        } else {
          throw new Error('Refresh request did not return success');
        }
      } catch (err) {
        console.error('Failed to refresh WatConnect token:', err);
        throw err;
      } finally {
        refreshPromise = null;
      }
    })();

    await refreshPromise;
    this.token = localStorage.getItem('watconnect_token');
    this.refreshToken = localStorage.getItem('watconnect_refresh_token');
  }

  async login(credentials) {
    try {
      const loginUrl = `${constants.Watconnect_API_BASE_URL}/api/auth/login`;

      const res = await axios.post(loginUrl, credentials, {
        headers: { 'Content-Type': 'application/json' },
      });

      const result = res.data;
      if (result.success) {
        this.setToken(result.authToken, result.refreshToken);
      }
      return result;
    } catch (error) {
      console.error('WatConnect login error:', error);
      throw error?.response?.data || error;
    }
  }

  async fetchAll(queryString = '') {
    await this.ensureValidToken();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    const res = await axios.get(url, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async fetchById(id) {
    await this.ensureValidToken();
    const url = id ? `${this.baseUrl}/${id}` : this.baseUrl;
    const res = await axios.get(url, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async create(data) {
    await this.ensureValidToken();
    const res = await axios.post(this.baseUrl, data, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async post(url, data) {
    await this.ensureValidToken();
    // Check if data is FormData
    const isFormData = data instanceof FormData;

    const headers = {
      Authorization: this.token,
    };

    // Don't set Content-Type for FormData, let axios handle it
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await axios.post(this.baseUrl + url, data, {
      headers,
      // Add CORS configuration
      withCredentials: false,
      timeout: 120000, // 30 second timeout for file uploads
    });
    return res.data;
  }

  async put(url, data) {
    await this.ensureValidToken();
    const res = await axios.put(this.baseUrl + url, data, {
      headers: { Authorization: this.token },
    });
    console.log('res', res);
    return res.data;
  }

  async get(url) {
    await this.ensureValidToken();
    const res = await axios.get(this.baseUrl + url, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async update(data, id) {
    await this.ensureValidToken();
    const res = await axios.put(`${this.baseUrl}/${id}`, data, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async delete(id) {
    await this.ensureValidToken();
    const res = await axios.delete(`${this.baseUrl}/${id}`, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async deleteUrl(url) {
    await this.ensureValidToken();
    const res = await axios.delete(this.baseUrl + url, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async deleteWithBody(url, data) {
    await this.ensureValidToken();
    const res = await axios.delete(this.baseUrl + url, {
      data,
      headers: {
        Authorization: this.token,
        'Content-Type': 'application/json',
      },
    });
    return res.data;
  }

  async search(params = {}) {
    await this.ensureValidToken();
    const { display_fields, ...otherParams } = params;
    const queryParams = {
      ...otherParams,
      ...(display_fields && { display_fields }),
    };

    const res = await axios.get(this.baseUrl, {
      params: queryParams,
      headers: { Authorization: this.token },
    });

    return res.data;
  }

  async upsert(data) {
    await this.ensureValidToken();
    const res = await axios.post(this.baseUrl, data, {
      headers: { Authorization: this.token },
    });
    return res.data;
  }

  async patch(url, data) {
    await this.ensureValidToken();
    // Detect if data is FormData
    const isFormData = data instanceof FormData;

    const headers = {
      Authorization: this.token,
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await axios.patch(this.baseUrl + url, data, {
      headers,
      withCredentials: false,
      timeout: 120000,
    });
    return res.data;
  }
}
